import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Brain,
  TrendingUp,
  Lightbulb,
  SlidersHorizontal,
  Target,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface OverviewData {
  distribution: { accurate: number; partial: number; inaccurate: number; total: number };
  weeklyTrend: Array<{ week: string; accurate: number; partial: number; inaccurate: number }>;
  activeAmendments: number;
  activeCalibrations: number;
  pendingAnalyses: number;
  scoreMatch: { accurate: number; close: number; miss: number; total: number };
}

interface Analysis {
  id: number;
  category: string;
  department: string | null;
  sampleSize: number;
  periodStart: string;
  periodEnd: string;
  analysis: {
    patterns?: Array<{ description: string; frequency: string; impact: string }>;
    scoringBias?: { direction: string; magnitude: number; explanation: string };
    promptSuggestions?: Array<{ instruction: string; rationale: string; confidence: number }>;
    summary?: string;
  };
  confidence: number;
  status: string;
  modelUsed: string;
  costUsd: number;
  createdAt: string;
}

interface Amendment {
  id: number;
  scope: string;
  instruction: string;
  rationale: string | null;
  confidence: number | null;
  active: boolean;
  source: string;
  timesApplied: number;
  effectivenessScore: number | null;
  createdAt: string;
}

interface Calibration {
  id: number;
  category: string;
  factor: number;
  biasDirection: string;
  sampleSize: number;
  confidence: number;
  active: boolean;
  createdAt: string;
}

// ─── Query Keys ────────────────────────────────────────────────────────────────

const queryKeys = {
  overview: ['learning', 'overview'] as const,
  analyses: ['learning', 'analyses'] as const,
  amendments: ['learning', 'amendments'] as const,
  calibration: ['learning', 'calibration'] as const,
};

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Learning() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'insights' | 'amendments' | 'calibration'>('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newScope, setNewScope] = useState('global');
  const [newInstruction, setNewInstruction] = useState('');
  const [newRationale, setNewRationale] = useState('');

  // ─── Queries ───────────────────────────────────────────────────────────

  const { data: overview, isLoading: overviewLoading } = useQuery<{ data: OverviewData }>({
    queryKey: queryKeys.overview,
    queryFn: async () => {
      const res = await fetch('/api/learning/overview');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: activeTab === 'overview',
  });

  const { data: analyses, isLoading: analysesLoading } = useQuery<{ data: Analysis[] }>({
    queryKey: queryKeys.analyses,
    queryFn: async () => {
      const res = await fetch('/api/learning/analyses');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: activeTab === 'insights',
  });

  const { data: amendments, isLoading: amendmentsLoading } = useQuery<{ data: Amendment[] }>({
    queryKey: queryKeys.amendments,
    queryFn: async () => {
      const res = await fetch('/api/learning/amendments');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: activeTab === 'amendments',
  });

  const { data: calibrations, isLoading: calibrationsLoading } = useQuery<{ data: Calibration[] }>({
    queryKey: queryKeys.calibration,
    queryFn: async () => {
      const res = await fetch('/api/learning/calibration');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: activeTab === 'calibration',
  });

  // ─── Mutations ─────────────────────────────────────────────────────────

  const runAnalysis = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/learning/hypervisor/run', { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: (data) => {
      showToast(`Analysis complete: ${data.data.analysesCreated} created, ${data.data.amendmentsCreated} amendments`, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.analyses });
      queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
    onError: () => showToast('Analysis failed', 'error'),
  });

  const reviewAnalysis = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'apply' | 'dismiss' }) => {
      const res = await fetch(`/api/learning/analyses/${id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: (_, { action }) => {
      showToast(`Analysis ${action === 'apply' ? 'applied' : 'dismissed'}`, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.analyses });
      queryClient.invalidateQueries({ queryKey: queryKeys.amendments });
      queryClient.invalidateQueries({ queryKey: queryKeys.calibration });
    },
    onError: () => showToast('Review failed', 'error'),
  });

  const toggleAmendment = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      const res = await fetch(`/api/learning/amendments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.amendments });
      queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
    onError: () => showToast('Toggle failed', 'error'),
  });

  const deleteAmendment = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/learning/amendments/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      showToast('Amendment deleted', 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.amendments });
    },
    onError: () => showToast('Delete failed', 'error'),
  });

  const createAmendment = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/learning/amendments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: newScope, instruction: newInstruction, rationale: newRationale }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      showToast('Amendment created', 'success');
      setShowAddModal(false);
      setNewInstruction('');
      setNewRationale('');
      queryClient.invalidateQueries({ queryKey: queryKeys.amendments });
    },
    onError: () => showToast('Create failed', 'error'),
  });

  const toggleCalibration = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      const res = await fetch(`/api/learning/calibration/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calibration });
      queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
    onError: () => showToast('Toggle failed', 'error'),
  });

  // ─── Tab Config ────────────────────────────────────────────────────────

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: TrendingUp },
    { id: 'insights' as const, label: 'Insights', icon: Lightbulb },
    { id: 'amendments' as const, label: 'Amendments', icon: SlidersHorizontal },
    { id: 'calibration' as const, label: 'Calibration', icon: Target },
  ];

  const ov = overview?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-500" />
            AI Learning System
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Feedback-driven prompt amendments and scoring calibration
          </p>
        </div>
      </div>

      {/* Tab buttons */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ─── Overview Tab ───────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {overviewLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : ov ? (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard label="Total Feedback" value={ov.distribution.total} />
                <StatCard label="Accuracy Rate" value={ov.distribution.total > 0 ? `${((ov.distribution.accurate / ov.distribution.total) * 100).toFixed(0)}%` : 'N/A'} color="green" />
                <StatCard label="Active Amendments" value={ov.activeAmendments} color="purple" />
                <StatCard label="Pending Reviews" value={ov.pendingAnalyses} color="yellow" />
              </div>

              {/* Accuracy distribution */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Accuracy Distribution</h3>
                <div className="flex gap-4">
                  <DistBar label="Accurate" count={ov.distribution.accurate} total={ov.distribution.total} color="bg-green-500" />
                  <DistBar label="Partial" count={ov.distribution.partial} total={ov.distribution.total} color="bg-yellow-500" />
                  <DistBar label="Inaccurate" count={ov.distribution.inaccurate} total={ov.distribution.total} color="bg-red-500" />
                </div>
              </div>

              {/* Weekly trend */}
              {ov.weeklyTrend.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Weekly Trend</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 dark:text-gray-400">
                          <th className="text-left py-2 pr-4">Week</th>
                          <th className="text-right py-2 px-4">Accurate</th>
                          <th className="text-right py-2 px-4">Partial</th>
                          <th className="text-right py-2 px-4">Inaccurate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ov.weeklyTrend.map((w) => (
                          <tr key={w.week} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{w.week}</td>
                            <td className="py-2 px-4 text-right text-green-600">{w.accurate}</td>
                            <td className="py-2 px-4 text-right text-yellow-600">{w.partial}</td>
                            <td className="py-2 px-4 text-right text-red-600">{w.inaccurate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* US-177: Score Agreement — Arbie vs human SR ratings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Score Agreement</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Arbie vs. human SR ratings
                  </span>
                </div>
                {ov.scoreMatch.total === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">
                    No comparisons yet — score agreement is recorded when a recruiter rates a candidate in SmartRecruiters.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      {ov.scoreMatch.total} comparison{ov.scoreMatch.total !== 1 ? 's' : ''} with human ratings
                      {ov.scoreMatch.total > 0 && ` · ${((ov.scoreMatch.accurate / ov.scoreMatch.total) * 100).toFixed(0)}% exact match`}
                    </p>
                    <div className="flex gap-4">
                      <DistBar label="Accurate" count={ov.scoreMatch.accurate} total={ov.scoreMatch.total} color="bg-green-500" />
                      <DistBar label="Close" count={ov.scoreMatch.close} total={ov.scoreMatch.total} color="bg-yellow-400" />
                      <DistBar label="Miss" count={ov.scoreMatch.miss} total={ov.scoreMatch.total} color="bg-red-500" />
                    </div>
                    <div className="mt-3 text-xs text-gray-400 dark:text-gray-500 space-y-0.5">
                      <p><span className="font-medium text-green-600">Accurate</span> — within 0.5 pts (same whole number)</p>
                      <p><span className="font-medium text-yellow-600">Close</span> — within 1.5 pts (one number off)</p>
                      <p><span className="font-medium text-red-600">Miss</span> — 1.5+ pts apart (also triggers re-review flag at 2+)</p>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">No feedback data available yet</div>
          )}
        </div>
      )}

      {/* ─── Insights Tab ───────────────────────────────────────────────── */}
      {activeTab === 'insights' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => runAnalysis.mutate()}
              disabled={runAnalysis.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
            >
              {runAnalysis.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run Analysis Now
            </button>
          </div>

          {analysesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : analyses?.data && analyses.data.length > 0 ? (
            analyses.data.map((a) => (
              <div key={a.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{a.category === 'global' ? 'Global Analysis' : a.department || a.category}</h3>
                      <StatusBadge status={a.status} />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {a.sampleSize} samples | Confidence: {(a.confidence * 100).toFixed(0)}% | Cost: ${a.costUsd?.toFixed(4) || '0'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {new Date(a.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  {a.status === 'pending_review' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => reviewAnalysis.mutate({ id: a.id, action: 'apply' })}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded hover:bg-green-200"
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => reviewAnalysis.mutate({ id: a.id, action: 'dismiss' })}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded hover:bg-gray-200"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>

                {a.analysis.summary && (
                  <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{a.analysis.summary}</p>
                )}

                {a.analysis.patterns && a.analysis.patterns.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Patterns</h4>
                    <ul className="text-sm space-y-1">
                      {a.analysis.patterns.map((p, i) => (
                        <li key={i} className="text-gray-600 dark:text-gray-400">- {p.description} ({p.frequency})</li>
                      ))}
                    </ul>
                  </div>
                )}

                {a.analysis.scoringBias && a.analysis.scoringBias.direction !== 'none' && (
                  <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm">
                    <span className="font-medium text-yellow-800 dark:text-yellow-300">Scoring Bias: </span>
                    <span className="text-yellow-700 dark:text-yellow-400">{a.analysis.scoringBias.direction.replace('_', '-')} ({(a.analysis.scoringBias.magnitude * 100).toFixed(0)}%)</span>
                    <span className="text-yellow-600 dark:text-yellow-500"> - {a.analysis.scoringBias.explanation}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">No analyses yet. Click "Run Analysis Now" to start.</div>
          )}
        </div>
      )}

      {/* ─── Amendments Tab ─────────────────────────────────────────────── */}
      {activeTab === 'amendments' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Manual
            </button>
          </div>

          {amendmentsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : amendments?.data && amendments.data.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Active</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Scope</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Instruction</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Source</th>
                    <th className="text-right py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Applied</th>
                    <th className="text-right py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {amendments.data.map((a) => (
                    <tr key={a.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-3 px-4">
                        <button onClick={() => toggleAmendment.mutate({ id: a.id, active: !a.active })}>
                          {a.active ? <ToggleRight className="h-5 w-5 text-purple-500" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">{a.scope}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-700 dark:text-gray-300 max-w-md truncate">{a.instruction}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs ${a.source === 'hypervisor' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500'}`}>{a.source}</span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500">{a.timesApplied}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => deleteAmendment.mutate(a.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No amendments yet</div>
          )}

          {/* Add Manual Modal */}
          {showAddModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg shadow-xl">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add Manual Amendment</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scope</label>
                    <select
                      value={newScope}
                      onChange={(e) => setNewScope(e.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="global">Global</option>
                      <option value="department:Engineering">Department: Engineering</option>
                      <option value="department:Marketing">Department: Marketing</option>
                      <option value="department:Sales">Department: Sales</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instruction</label>
                    <textarea
                      value={newInstruction}
                      onChange={(e) => setNewInstruction(e.target.value)}
                      rows={3}
                      placeholder="E.g., Weight cloud certifications (AWS, GCP, Azure) more heavily for this department"
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rationale (optional)</label>
                    <input
                      value={newRationale}
                      onChange={(e) => setNewRationale(e.target.value)}
                      placeholder="Why this amendment is needed"
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => createAmendment.mutate()}
                    disabled={!newInstruction.trim() || createAmendment.isPending}
                    className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Calibration Tab ────────────────────────────────────────────── */}
      {activeTab === 'calibration' && (
        <div className="space-y-4">
          {calibrationsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : calibrations?.data && calibrations.data.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Active</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Category</th>
                    <th className="text-right py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Factor</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Bias</th>
                    <th className="text-right py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Samples</th>
                    <th className="text-right py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {calibrations.data.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-3 px-4">
                        <button onClick={() => toggleCalibration.mutate({ id: c.id, active: !c.active })}>
                          {c.active ? <ToggleRight className="h-5 w-5 text-purple-500" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{c.category}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        <span className={c.factor < 1 ? 'text-red-600' : c.factor > 1 ? 'text-green-600' : 'text-gray-600'}>
                          {c.factor.toFixed(2)}x
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          c.biasDirection === 'over_scoring' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                          : c.biasDirection === 'under_scoring' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {c.biasDirection.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500">{c.sampleSize}</td>
                      <td className="py-3 px-4 text-right text-gray-500">{(c.confidence * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 space-y-3">
              <p>No calibrations yet. Run a hypervisor analysis to detect scoring biases.</p>
              <button
                onClick={() => runAnalysis.mutate()}
                disabled={runAnalysis.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
              >
                {runAnalysis.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Run Analysis Now
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  const colorClass = color === 'green' ? 'text-green-600' : color === 'purple' ? 'text-purple-600' : color === 'yellow' ? 'text-yellow-600' : 'text-gray-900 dark:text-gray-100';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}

function DistBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex-1">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-900 dark:text-gray-100 font-medium">{count} ({pct.toFixed(0)}%)</span>
      </div>
      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    applied: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    dismissed: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  const icons: Record<string, typeof CheckCircle> = {
    pending_review: Clock,
    applied: CheckCircle,
    dismissed: XCircle,
  };
  const Icon = icons[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${styles[status] || styles.pending_review}`}>
      <Icon className="h-3 w-3" />
      {status.replace('_', ' ')}
    </span>
  );
}
